/**
 * Het overdrachtsblok van motor 7 boven het gesprek (Brain grote-ronde/75
 * keuze 6, OPEN 31). Bron: `emails.motor7_overdracht` via `GET .../midden`.
 *
 * Vier delen, elk alleen als er iets in staat:
 * - Beslis: wat Sanne aan JP voorlegt (doorgezet: onderwerp en voorstel), of een
 *   handeling die klaarstaat;
 * - Gebeurt als jij verstuurt: de beloftes en aangevraagde handelingen uit de
 *   uitgang in wacht (keuze 1 van 74: pas bij versturen);
 * - Nalezing: het oordeel van de meelezer, of dat hij ontbrak (keuze 10 van 74);
 * - Alarm: een geweigerde handeling of een later oordeel "terug".
 *
 * Geen blok: niets tonen, ook geen lege kop. Op de iPhone staat het bovenaan
 * de zaak en klapt het in met de kop.
 */
import { useState } from "react";

import { Icoon } from "../Icoon";
import type { M7Midden } from "./middenApi";

interface Regel {
  tekst: string;
  uitleg: string | null;
}

/** Wat in het blok staat, of null als er niets te tonen is. */
export function overdrachtInhoud(midden: M7Midden | null | undefined) {
  const blok = midden?.overdracht;
  if (!blok) return null;
  const naam = (n: string) => midden.handelingen?.[n] ?? n;

  const beslis: Regel[] = [
    ...(blok.doorgezet ?? []).map((d) => ({ tekst: d.voorstel, uitleg: d.onderwerp || null })),
    ...(blok.handelingen ?? [])
      .filter((h) => h.uitslag === "klaargezet")
      .map((h) => ({ tekst: naam(h.naam), uitleg: h.reden })),
  ].filter((r) => r.tekst.trim());

  const bij = blok.bij_versturen ?? null;
  const versturen: Regel[] = [
    ...(bij?.beloftes ?? []).map((b) => ({
      tekst: b.zin,
      uitleg: "belofte aan de klant; de wekker gaat pas na versturen",
    })),
    ...(bij?.handelingen ?? []).map((h) => ({
      tekst: naam(h.naam),
      uitleg: h.onderbouwing ? `${h.onderbouwing}; gebeurt pas na versturen` : "gebeurt pas na versturen",
    })),
  ].filter((r) => r.tekst.trim());

  const m = blok.meelezer ?? null;
  const later = m?.later ?? null;
  let nalezing: { tekst: string; let: boolean };
  if (m?.ontbrak) {
    nalezing = { tekst: "Meelezer ontbrak, jij kijkt extra na.", let: true };
    if (later?.oordeel === "goed") {
      nalezing = { tekst: "Meelezer was te laat; later gelezen, niets afgekeurd.", let: false };
    }
  } else if (midden.meelezer?.oordeel === "goed") {
    nalezing = { tekst: "Nagelezen, niets afgekeurd.", let: false };
  } else if (midden.meelezer?.oordeel === "terug") {
    const soorten = (midden.meelezer.soorten ?? []).join(", ");
    nalezing = {
      tekst: `Nagelezen; de meelezer had opmerkingen${soorten ? ` (${soorten})` : ""}.`,
      let: true,
    };
  } else {
    nalezing = { tekst: "Nalezing onbekend.", let: false };
  }

  const alarm: string[] = [
    ...(blok.handelingen ?? [])
      .filter((h) => h.uitslag === "geweigerd")
      .map((h) => `Geweigerd: ${naam(h.naam)}${h.reden ? `, ${h.reden}` : ""}.`),
    ...(later?.oordeel === "terug"
      ? [
          `De meelezer las het concept later en keurde af${
            later.opmerkingen?.length
              ? `: ${later.opmerkingen.map((o) => o.reden || o.zin).join("; ")}`
              : ""
          }.`,
        ]
      : []),
  ];

  return { beslis, versturen, nalezing, alarm };
}

export function Overdracht({ midden, van = "Sanne" }: { midden: M7Midden | null | undefined; van?: string }) {
  const [open, setOpen] = useState(true);
  const inhoud = overdrachtInhoud(midden);
  if (!inhoud) return null;
  const { beslis, versturen, nalezing, alarm } = inhoud;
  return (
    <section className="m7-overdracht" aria-label="Overdracht" data-testid="m7-overdracht">
      <button type="button" className="ok" aria-expanded={open} onClick={() => setOpen(!open)}>
        <Icoon naam="antwoord" klein />
        <span>Van {van} naar jou</span>
        <span className="klap" aria-hidden="true">
          <Icoon naam={open ? "omhoog" : "pijl"} klein />
        </span>
      </button>
      {open && (
        <>
          {beslis.map((r, i) => (
            <div key={`b${i}`} className="beslis">
              <Icoon naam="uitroep" klein />
              <span>
                <b>Beslis:</b> {r.tekst}
                {r.uitleg && <span className="q">{r.uitleg}</span>}
              </span>
            </div>
          ))}
          {versturen.length > 0 && (
            <div className="vakje">
              <span className="kopje">
                <Icoon naam="klok" klein />
                Gebeurt als jij verstuurt
              </span>
              <ul>
                {versturen.map((r, i) => (
                  <li key={i}>
                    <span className="st" />
                    <span>
                      {r.tekst}
                      {r.uitleg && <span className="q">{r.uitleg}</span>}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className={`nalezing${nalezing.let ? " alarm" : ""}`} data-testid="m7-nalezing">
            <Icoon naam="kennis" klein />
            <span>{nalezing.tekst}</span>
          </div>
          {alarm.map((a, i) => (
            <div key={`a${i}`} className="nalezing alarm" role="alert">
              <Icoon naam="uitroep" klein />
              <span>{a}</span>
            </div>
          ))}
        </>
      )}
    </section>
  );
}

/** De zinnen die in het concept gemarkeerd worden: de goedgekeurde beloftezinnen. */
export function lijstZinnen(midden: M7Midden | null | undefined): string[] {
  return (midden?.overdracht?.bij_versturen?.beloftes ?? [])
    .map((b) => b.zin.trim())
    .filter(Boolean);
}
